// Generated by GitHub Copilot
// US1794882: Brand Management Component
import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from 'prop-types';
import { FaEdit, FaTrash } from "react-icons/fa";
import styles from "./BrandManager.module.css";
import ConfirmationModal from '../Modern/ConfirmationModal';
import RefreshIndicator from '../Modern/Incidents/components/RefreshIndicator';
import usePolling from '../../hooks/usePolling';
import { POLLING_INTERVALS } from '../../config/polling';
import { BrandsAPI } from "../../connections/Brands";
import { EscalationAPI } from "../../connections/fetchEscalations";
import { trackBrandOperation } from '../../utils/modernUITracking';
import SimpleMenu from "../SimpleMenu";

// PagerDuty workflow filter — scoped to EOC Business Liaison role per US1929209
const WORKFLOW_FILTER = 'EOC Business Liaison';

function BrandManager({ user }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [currentBrand, setCurrentBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    workflows: [],
  });
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);

  // Ref for modal focus trap
  const modalRef = useRef(null);

  // Memoized modal click handler to prevent event propagation
  const handleModalClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Check if user has configuration or deity privilege
  const hasManagePrivilege =
    user &&
    user.privileges &&
    (user.privileges.configuration || user.privileges.deity);

  // Fetch all brands on mount
  const fetchBrands = async (isBackgroundRefresh = false) => {
    if (isBackgroundRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await BrandsAPI.get();
      setBrands(data.brands || []);
      setLastUpdated(new Date());
      setIsInitialLoad(false);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch brands:", err);
      if (isBackgroundRefresh) {
        console.warn('Background refresh failed, keeping existing data:', err);
      } else {
        setError(err.message || "Failed to load brands. Please try again.");
      }
    } finally {
      if (isBackgroundRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  // US1929209: Fetch PagerDuty workflows for the multi-select
  const fetchWorkflows = async () => {
    setWorkflowsLoading(true);
    try {
      const data = await EscalationAPI.getWorkflows(WORKFLOW_FILTER);
      const options = data && data.workflows
        ? data.workflows.map((w) => ({ value: w.id, label: w.name }))
        : [];
      setWorkflowOptions(options);
    } catch (err) {
      console.error("Failed to fetch workflows:", err);
      setWorkflowOptions([]);
    } finally {
      setWorkflowsLoading(false);
    }
  };

  useEffect(() => {
    if (hasManagePrivilege) {
      fetchBrands();
    }
  }, [hasManagePrivilege]);

  // Background auto-refresh
  usePolling(
    () => fetchBrands(true),
    POLLING_INTERVALS.BRAND_MANAGER,
    {
      enabled: !isInitialLoad && !loading && hasManagePrivilege,
      pauseWhenHidden: true
    }
  );

  // Focus trap for modal
  useEffect(() => {
    if (!showModal || !modalRef.current) return;

    const modalElement = modalRef.current;

    // Get all focusable elements within the modal
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Set initial focus on first input
    const firstInput = modalElement.querySelector('input[type="text"]');
    if (firstInput) {
      firstInput.focus();
    } else {
      firstFocusable.focus();
    }

    // Handle tab key to trap focus
    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    // Add event listener
    modalElement.addEventListener('keydown', handleTabKey);

    // Cleanup
    return () => {
      modalElement.removeEventListener('keydown', handleTabKey);
    };
  }, [showModal]);

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === "") {
      errors.name = "Brand name is required";
    } else if (formData.name.length > 50) {
      errors.name = "Brand name must be 50 characters or less";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Open modal for creating a new brand
  const handleCreate = () => {
    setModalMode("create");
    setCurrentBrand(null);
    setFormData({ name: "", workflows: [] });
    setFormErrors({});
    setShowModal(true);
    fetchWorkflows();

    // Track modal open
    trackBrandOperation('ModalOpen', {
      mode: 'create'
    });
  };

  // Open modal for editing an existing brand
  const handleEdit = (brand) => {
    setModalMode("edit");
    setCurrentBrand(brand);
    setFormData({
      name: brand.name,
      workflows: brand.workflows || [],
    });
    // Pre-seed options with the brand's existing workflows so defaultValue
    // resolves on mount instead of flashing blank while the full fetch loads.
    setWorkflowOptions(
      (brand.workflows || []).map((w) => ({ value: w.id, label: w.name }))
    );
    setFormErrors({});
    setShowModal(true);
    fetchWorkflows();

    // Track modal open
    trackBrandOperation('ModalOpen', {
      mode: 'edit',
      brandId: brand.id,
      brandName: brand.name
    });
  };

  // Delete a brand
  const handleDelete = async () => {
    if (!brandToDelete) return;

    const brand = brandToDelete;
    setShowDeleteModal(false);
    setBrandToDelete(null);

    try {
      await BrandsAPI.delete(brand.id);

      // Track successful deletion
      trackBrandOperation('Delete', {
        brandId: brand.id,
        brandName: brand.name,
        success: true
      });

      // Success - refresh the list
      await fetchBrands();
    } catch (err) {
      console.error("Delete error:", err);

      // Track failed deletion
      trackBrandOperation('Delete', {
        brandId: brand.id,
        brandName: brand.name,
        success: false,
        error: err.message
      });

      setError(err.message || "Failed to delete brand. Please try again.");
    }
  };

  // Close modal
  const handleCloseModal = () => {
    // Track modal close
    trackBrandOperation('ModalClose', {
      mode: modalMode,
      brandId: currentBrand?.id,
      brandName: currentBrand?.name
    });

    setShowModal(false);
    setCurrentBrand(null);
    setFormData({ name: "", workflows: [] });
    setFormErrors({});
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      let brandId = currentBrand?.id;
      let brandName = formData.name;

      if (modalMode === "create") {
        const result = await BrandsAPI.create(formData);
        brandId = result?.id || brandId;

        // Track successful creation
        trackBrandOperation('Create', {
          brandId,
          brandName,
          success: true
        });
      } else {
        await BrandsAPI.update(currentBrand.id, formData);

        // Track successful edit
        trackBrandOperation('Edit', {
          brandId: currentBrand.id,
          brandName,
          success: true
        });
      }

      // Success - refresh the list and close modal
      await fetchBrands();
      handleCloseModal();
    } catch (err) {
      console.error("Submit error:", err);

      // Track failed operation
      trackBrandOperation(modalMode === "create" ? 'Create' : 'Edit', {
        brandId: currentBrand?.id,
        brandName: formData.name,
        success: false,
        error: err.message
      });

      setError(
        err.message ||
          `Failed to ${
            modalMode === "create" ? "create" : "update"
          } brand. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  if (!hasManagePrivilege) {
    return (
      <div className={`${styles.alert} ${styles.alertWarning}`}>
        <div>
          <h3 className={styles.alertHeading}>Access Restricted</h3>
          <p className={styles.alertText}>
            You need configuration or administrator privileges to manage brands.
          </p>
          <hr style={{ margin: "1rem 0", border: "none", borderTop: "1px solid var(--border-subtle)" }} />
          <p className={styles.alertText}>
            <small>
              Contact your administrator if you need access to this feature.
            </small>
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} role="status" aria-label="Loading">
          <span style={{ position: "absolute", width: "1px", height: "1px", overflow: "hidden" }}>
            Loading brands...
          </span>
        </div>
        <p className={styles.loadingText}>Loading brands...</p>
      </div>
    );
  }

  return (
    <div className={styles.brandManager}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h5 className={styles.cardTitle}>Brand Awareness</h5>
          <div className={styles.headerActions}>
            <span className={styles.badge}>
              {brands.length} {brands.length === 1 ? "brand" : "brands"}
            </span>
            <RefreshIndicator
              isRefreshing={refreshing}
              lastUpdated={lastUpdated}
              showTimestamp={!refreshing && !!lastUpdated}
            />
            <button className={styles.btnPrimary} onClick={handleCreate}>
              + Add Brand
            </button>
          </div>
        </div>
        <div className={styles.cardBody}>
          {error && (
            <div className={`${styles.alert} ${styles.alertDanger}`}>
              <div style={{ flex: 1 }}>
                {error}
              </div>
              <button
                className={styles.alertDismiss}
                onClick={() => setError(null)}
                aria-label="Dismiss alert"
              >
                ×
              </button>
            </div>
          )}

          {brands.length === 0 ? (
            <div className={`${styles.alert} ${styles.alertInfo}`}>
              <p className={styles.alertText}>
                No brands have been created yet. Click "Add Brand" to create
                your first brand.
              </p>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Workflows</th>
                    <th>Created</th>
                    <th>Modified</th>
                    <th>Modified By</th>
                    <th style={{ width: "100px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {brands.map((brand) => (
                    <tr key={brand.id}>
                      <td data-label="Name">
                        <span className={styles.brandName}>{brand.name}</span>
                      </td>
                      <td data-label="Workflows">
                        <span className={styles.timestamp}>
                          {brand.workflows?.length
                            ? `${brand.workflows.length} workflow(s)`
                            : "None"}
                        </span>
                      </td>
                      <td data-label="Created">
                        <span className={styles.timestamp}>
                          {formatTimestamp(brand.createdAt)}
                        </span>
                      </td>
                      <td data-label="Modified">
                        <span className={styles.timestamp}>
                          {formatTimestamp(brand.modifiedAt)}
                        </span>
                      </td>
                      <td data-label="Modified By">
                        <span className={styles.timestamp}>{brand.modifiedBy}</span>
                      </td>
                      <td data-label="Actions">
                        <div className={styles.actionButtons}>
                          <button
                            className={`${styles.iconButton} ${styles.iconButtonPrimary}`}
                            onClick={() => handleEdit(brand)}
                            aria-label={`Edit ${brand.name}`}
                            title="Edit"
                          >
                            <FaEdit />
                            <span className={styles.buttonLabel}>Edit</span>
                          </button>
                          <button
                            className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                            onClick={() => {
                              setBrandToDelete(brand);
                              setShowDeleteModal(true);
                            }}
                            aria-label={`Delete ${brand.name}`}
                            title="Delete"
                          >
                            <FaTrash />
                            <span className={styles.buttonLabel}>Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div
            className={styles.modal}
            onClick={handleModalClick}
            ref={modalRef}
            role="dialog"
            aria-modal="true"
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {modalMode === "create" ? "Create New Brand" : "Edit Brand"}
              </h2>
              <button
                className={styles.modalClose}
                onClick={handleCloseModal}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="brandName">
                    Brand Name <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="brandName"
                    type="text"
                    name="name"
                    className={`${styles.formInput} ${formErrors.name ? styles.invalid : ""}`}
                    value={formData.name}
                    onChange={handleInputChange}
                    maxLength={50}
                    placeholder="Enter brand name (max 50 characters)"
                    disabled={submitting}
                    autoFocus
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {formErrors.name ? (
                      <p className={styles.formError}>
                        {formErrors.name}
                      </p>
                    ) : (
                      <p className={styles.formHelp}>
                        Required field
                      </p>
                    )}
                    <span className={`${styles.characterCount} ${formData.name.length > 45 ? styles.warning : ""}`}>
                      {formData.name.length}/50
                    </span>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="brandWorkflows">
                    PagerDuty Workflows
                  </label>
                  <SimpleMenu
                    id="brandWorkflows"
                    multiple={true}
                    options={workflowOptions}
                    defaultValue={
                      formData.workflows
                        .map((w) => workflowOptions.find((o) => o.value === w.id))
                        .filter(Boolean)
                    }
                    onChange={(selected) => {
                      const workflows = (selected || []).map((s) => ({
                        id: s.value,
                        name: s.label,
                      }));
                      setFormData((prev) => ({ ...prev, workflows }));
                    }}
                    disabled={submitting}
                    loading={workflowsLoading}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1400 }) }}
                  />
                  <p className={styles.formHelp}>
                    Select PagerDuty workflows to associate with this brand
                  </p>
                </div>

                {currentBrand && (
                  <div className={`${styles.alert} ${styles.alertInfo}`}>
                    <p className={styles.alertText} style={{ margin: 0 }}>
                      <strong>Created:</strong>{" "}
                      {formatTimestamp(currentBrand.createdAt)}
                      <br />
                      <strong>Last Modified:</strong>{" "}
                      {formatTimestamp(currentBrand.modifiedAt)} by{" "}
                      {currentBrand.modifiedBy}
                    </p>
                  </div>
                )}
              </form>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.btnSecondary}
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <span className={styles.btnWithSpinner}>
                    <span className={styles.btnSpinner} aria-hidden="true" />
                    {modalMode === "create" ? "Creating..." : "Saving..."}
                  </span>
                ) : modalMode === "create" ? (
                  "Create Brand"
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setBrandToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Brand"
        message={`Are you sure you want to delete "${brandToDelete?.name || 'this brand'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

BrandManager.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
    privileges: PropTypes.shape({
      configuration: PropTypes.bool,
      deity: PropTypes.bool
    })
  })
};

export default BrandManager;
